import { usePredictionParams } from './predictionParams'
import { useFetch } from './fetch'
import { useExporter } from './exporter'

import type { PredictionDataSanitised } from '~/types/api'

export const usePercentiles = createGlobalState(() => {
  const { predictionParameters } = usePredictionParams()

  const { resetComment } = useExporter()

  const { data: predictionData, isFetching, execute, onFetchResponse } = useFetch('/prediction/data', { headers: { 'Content-Type': 'application/json' } }, { immediate: false }).json<PredictionDataSanitised | null | undefined>().post(predictionParameters)

  const currentPercentiles = ref<Record<keyof PredictionDataSanitised, number | undefined>>({
    damagesPredictions: undefined,
    settlementTimePredictions: undefined,
    thirdPartyCostsPredictions: undefined,
  })

  const getValueFromPercentile = <T extends number | undefined>(type: keyof PredictionDataSanitised, percentile: T) => {
    if (!predictionData.value || typeof percentile !== 'number')
      return undefined as T

    return predictionData.value[type]?.percentilePredictions?.find(p => p.percentile === percentile)?.prediction as T
  }

  const settlementDays = computed(() => getValueFromPercentile('settlementTimePredictions', currentPercentiles.value.settlementTimePredictions))

  const damages = computed(() => getValueFromPercentile('damagesPredictions', currentPercentiles.value.damagesPredictions))

  const thirdPartyCosts = computed(() => getValueFromPercentile('thirdPartyCostsPredictions', currentPercentiles.value.thirdPartyCostsPredictions))

  const recommendation = computed(() => {
    const damages = getValueFromPercentile('damagesPredictions', currentPercentiles.value.damagesPredictions)
    const thirdPartyCosts = getValueFromPercentile('thirdPartyCostsPredictions', currentPercentiles.value.thirdPartyCostsPredictions)

    if (damages === undefined || thirdPartyCosts === undefined)
      return undefined

    return damages + thirdPartyCosts
  })

  const isAiPrediction = computed(() => ({
    damagesPredictions: predictionData.value?.damagesPredictions?.aiPredictionPercentile === currentPercentiles.value.damagesPredictions,
    settlementTimePredictions: predictionData.value?.settlementTimePredictions?.aiPredictionPercentile === currentPercentiles.value.settlementTimePredictions,
    thirdPartyCostsPredictions: predictionData.value?.thirdPartyCostsPredictions?.aiPredictionPercentile === currentPercentiles.value.thirdPartyCostsPredictions,
  }))

  const resetPrediction = (key: keyof PredictionDataSanitised) => {
    currentPercentiles.value[key] = predictionData.value?.[key]?.aiPredictionPercentile ?? 50
  }

  const runPrediction = async () => {
    predictionData.value = null
    resetComment()
    await execute()
  }

  onFetchResponse(() => {
    if (!predictionData.value)
      return
    currentPercentiles.value = {
      damagesPredictions: predictionData.value.damagesPredictions?.aiPredictionPercentile ?? 50,
      settlementTimePredictions: predictionData.value.settlementTimePredictions?.aiPredictionPercentile ?? 50,
      thirdPartyCostsPredictions: predictionData.value.thirdPartyCostsPredictions?.aiPredictionPercentile ?? 50,
    }
  })

  return {
    currentPercentiles,
    predictionData,
    isFetching,
    recommendation,
    settlementDays,
    damages,
    thirdPartyCosts,
    isAiPrediction,
    runPrediction,
    getValueFromPercentile,
    resetPrediction,
  }
})
